import { useState, useRef, useEffect } from 'react';
import { useLanguage, Language } from '@/contexts/LanguageContext';
import { Globe } from 'lucide-react';

interface Props {
  scrolled: boolean;
}

export default function LanguageSwitcher({ scrolled }: Props) {
  const { language, setLanguage, languages, languageFlags } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Select language"
        className={`flex items-center gap-1.5 text-sm transition-colors duration-300 ${
          scrolled ? 'text-foreground' : 'text-primary-foreground'
        }`}
      >
        <Globe size={16} aria-hidden="true" />
        <span>{languageFlags[language]}</span>
      </button>
      {open && (
        <ul className="absolute right-0 top-full mt-2 bg-background border border-border shadow-xl rounded-sm py-1 min-w-[140px] z-50 list-none m-0 p-0" role="menu" aria-label="Language options">
          {languages.map((lang) => (
            <li key={lang} role="none">
              <button
                onClick={() => { setLanguage(lang as Language); setOpen(false); }}
                role="menuitem"
                className={`w-full text-left px-4 py-2 text-sm hover:bg-secondary transition-colors flex items-center gap-2 ${
                  language === lang ? 'text-accent font-medium' : 'text-foreground'
                }`}
              >
                <span aria-hidden="true">{languageFlags[lang as Language]}</span>
                {lang.toUpperCase()}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
